/**
 * Perspective (4-point) warp utilities for the image document editor.
 * No external dependencies: homography via Gaussian elimination, warp via WebGL
 * with a CPU fallback.
 */

/** Load a File into a canvas, honoring EXIF orientation and capping the max dimension. */
export async function loadImageFromFile(file, maxDimension = 3000) {
    let bitmap;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
        bitmap = await new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
            img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
            img.src = url;
        });
    }
    const w = bitmap.width;
    const h = bitmap.height;
    const scale = Math.min(1, maxDimension / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === 'function') bitmap.close();
    return canvas;
}

/** Rotate a canvas 90 degrees counterclockwise. */
export function rotateCanvas90(source) {
    const rotated = document.createElement('canvas');
    rotated.width = source.height;
    rotated.height = source.width;
    const ctx = rotated.getContext('2d');
    ctx.translate(0, rotated.height);
    ctx.rotate(-Math.PI / 2);
    ctx.drawImage(source, 0, 0);
    return rotated;
}

/** Solve a linear system (n x n) via Gaussian elimination with partial pivoting. */
function solveLinearSystem(matrix, vector) {
    const n = vector.length;
    const a = matrix.map((row, i) => [...row, vector[i]]);
    for (let col = 0; col < n; col++) {
        let pivot = col;
        for (let row = col + 1; row < n; row++) {
            if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) pivot = row;
        }
        if (Math.abs(a[pivot][col]) < 1e-12) return null;
        [a[col], a[pivot]] = [a[pivot], a[col]];
        for (let row = 0; row < n; row++) {
            if (row === col) continue;
            const factor = a[row][col] / a[col][col];
            for (let k = col; k <= n; k++) {
                a[row][k] -= factor * a[col][k];
            }
        }
    }
    return a.map((row, i) => row[n] / a[i][i]);
}

/**
 * Homography H (3x3, row-major, h33 = 1) such that H * [x, y, 1]^T ~ [u, v, 1]^T
 * for each of the 4 point pairs (from[i] -> to[i]).
 */
export function computeHomography(from, to) {
    const m = [];
    const v = [];
    for (let i = 0; i < 4; i++) {
        const { x, y } = from[i];
        const { x: u, y: w } = to[i];
        m.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        v.push(u);
        m.push([0, 0, 0, x, y, 1, -w * x, -w * y]);
        v.push(w);
    }
    const h = solveLinearSystem(m, v);
    if (!h) return null;
    return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

/** Output size for a warped quad: longest opposing edges, capped. */
export function outputSizeForCorners(corners, maxDimension = 3500) {
    const [tl, tr, br, bl] = corners;
    let w = Math.round(Math.max(distance(tl, tr), distance(bl, br)));
    let h = Math.round(Math.max(distance(tl, bl), distance(tr, br)));
    w = Math.max(50, w);
    h = Math.max(50, h);
    const scale = Math.min(1, maxDimension / Math.max(w, h));
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

const VERTEX_SHADER = `
attribute vec2 a_pos;
void main() {
    gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision highp float;
uniform sampler2D u_tex;
uniform mat3 u_h;
uniform vec2 u_srcSize;
uniform vec2 u_outSize;
void main() {
    // gl_FragCoord has y=0 at the framebuffer bottom; dest coords are top-down.
    vec2 destPx = vec2(gl_FragCoord.x, u_outSize.y - gl_FragCoord.y);
    vec3 s = u_h * vec3(destPx, 1.0);
    vec2 src = s.xy / s.z;
    vec2 uv = src / u_srcSize;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
        gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0);
    } else {
        gl_FragColor = texture2D(u_tex, uv);
    }
}`;

function compileShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function warpWithWebGL(source, homography, outW, outH) {
    const glCanvas = document.createElement('canvas');
    glCanvas.width = outW;
    glCanvas.height = outH;
    const gl = glCanvas.getContext('webgl', { premultipliedAlpha: false, preserveDrawingBuffer: true });
    if (!gl) return null;
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    if (Math.max(source.width, source.height, outW, outH) > maxTex) return null;

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    const quad = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

    const h = homography;
    // Row-major -> WebGL column-major.
    gl.uniformMatrix3fv(gl.getUniformLocation(program, 'u_h'), false, [
        h[0], h[3], h[6],
        h[1], h[4], h[7],
        h[2], h[5], h[8],
    ]);
    gl.uniform2f(gl.getUniformLocation(program, 'u_srcSize'), source.width, source.height);
    gl.uniform2f(gl.getUniformLocation(program, 'u_outSize'), outW, outH);

    gl.viewport(0, 0, outW, outH);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    const output = document.createElement('canvas');
    output.width = outW;
    output.height = outH;
    output.getContext('2d').drawImage(glCanvas, 0, 0);

    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return output;
}

function warpWithCPU(source, homography, outW, outH) {
    const srcCtx = source.getContext('2d');
    const srcData = srcCtx.getImageData(0, 0, source.width, source.height);
    const src = srcData.data;
    const output = document.createElement('canvas');
    output.width = outW;
    output.height = outH;
    const outCtx = output.getContext('2d');
    const outData = outCtx.createImageData(outW, outH);
    const out = outData.data;
    const [h0, h1, h2, h3, h4, h5, h6, h7, h8] = homography;
    for (let y = 0; y < outH; y++) {
        for (let x = 0; x < outW; x++) {
            const w = h6 * x + h7 * y + h8;
            const sx = Math.round((h0 * x + h1 * y + h2) / w);
            const sy = Math.round((h3 * x + h4 * y + h5) / w);
            const outIdx = (y * outW + x) * 4;
            if (sx >= 0 && sx < source.width && sy >= 0 && sy < source.height) {
                const srcIdx = (sy * source.width + sx) * 4;
                out[outIdx] = src[srcIdx];
                out[outIdx + 1] = src[srcIdx + 1];
                out[outIdx + 2] = src[srcIdx + 2];
                out[outIdx + 3] = 255;
            } else {
                out[outIdx] = 255;
                out[outIdx + 1] = 255;
                out[outIdx + 2] = 255;
                out[outIdx + 3] = 255;
            }
        }
    }
    outCtx.putImageData(outData, 0, 0);
    return output;
}

/**
 * Warp the quad defined by `corners` ([topLeft, topRight, bottomRight, bottomLeft],
 * in source pixel coords) into a straightened rectangle canvas.
 */
export function warpPerspective(source, corners) {
    const { width, height } = outputSizeForCorners(corners);
    const destRect = [
        { x: 0, y: 0 },
        { x: width, y: 0 },
        { x: width, y: height },
        { x: 0, y: height },
    ];
    const homography = computeHomography(destRect, corners);
    if (!homography) return null;
    return warpWithWebGL(source, homography, width, height)
        || warpWithCPU(source, homography, width, height);
}
